import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDeviceKey, resetDeviceKey } from './lib/device';
import { db, persistSummary, persistTrial } from './lib/localDb';
import { computeSignature } from './lib/signature';
import { flushOutbox, getBootstrap, getCommons, startSession } from './lib/api';
import { useDriftStore } from './lib/store';
import type { TrialResult } from './lib/types';
import { TrialRunner } from './components/TrialRunner';
import { SignatureChart } from './components/SignatureChart';
import { CommonsPanel } from './components/CommonsPanel';
import { TurnstileBox } from './components/TurnstileBox';

const LINKEDIN_URL = 'https://www.linkedin.com/in/mindful-abhay/';
const COFFEE_URL = 'https://buymeacoffee.com/abhaypratapsingh';

function useOnlineSync() {
  const setSyncMessage = useDriftStore((s) => s.setSyncMessage);
  useEffect(() => {
    const sync = async () => {
      try {
        const sent = await flushOutbox();
        setSyncMessage(sent ? `synced ${sent} local item${sent === 1 ? '' : 's'}` : 'all local data synced');
      } catch {
        setSyncMessage('offline buffer active');
      }
    };
    void sync();
    window.addEventListener('online', sync);
    const id = window.setInterval(sync, 20000);
    return () => {
      window.removeEventListener('online', sync);
      window.clearInterval(id);
    };
  }, [setSyncMessage]);
}

export default function App() {
  const {
    bootstrap,
    setBootstrap,
    deviceKey,
    sessionId,
    deviceHashPrefix,
    schedule,
    trials,
    signature,
    commons,
    syncMessage,
    prepareRun,
    setSession,
    addTrial,
    setSignature,
    setCommons,
    setSyncMessage,
    resetRun
  } = useDriftStore();

  const [view, setView] = useState<'welcome' | 'run' | 'results'>('welcome');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [starting, setStarting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [commonsLoading, setCommonsLoading] = useState(false);
  const [error, setError] = useState('');

  useOnlineSync();

  useEffect(() => {
    getBootstrap()
      .then(setBootstrap)
      .catch(() => {
        setBootstrap({
          consentLine: 'I consent to anonymously store my timing responses and condition labels to build a public aggregate map; no name, email, or account is collected.',
          consentVersion: 'local',
          turnstileSiteKey: '',
          turnstileEnabled: false,
          appVersion: 'local'
        });
      });
  }, [setBootstrap]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('view') === 'commons') {
      void refreshCommons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentPlan = schedule[currentIndex];
  const completed = trials.length >= schedule.length && schedule.length > 0;
  const consentLine = bootstrap?.consentLine ?? 'Loading consent line…';

  const refreshCommons = useCallback(async () => {
    setCommonsLoading(true);
    try {
      const payload = await getCommons();
      setCommons(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load commons');
    } finally {
      setCommonsLoading(false);
    }
  }, [setCommons]);

  const beginRun = async () => {
    setStarting(true);
    setError('');
    try {
      const key = getDeviceKey();
      const planned = prepareRun(key);
      const consentVersion = bootstrap?.consentVersion ?? 'local';
      const response = await startSession({ deviceKey: key, consentVersion, turnstileToken });
      setSession(response.sessionId, response.deviceHashPrefix);
      await db.sessions.put({ id: response.sessionId, createdAt: new Date().toISOString(), deviceKey: key, consentVersion, synced: 1 });
      setCurrentIndex(0);
      setView('run');
      setSyncMessage(`run opened · ${planned.length} blind intervals`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the run');
    } finally {
      setStarting(false);
    }
  };

  const completeTrial = async (trial: TrialResult) => {
    addTrial(trial);
    await persistTrial(trial);
    setSyncMessage('saved locally; syncing millisecond row');
    void flushOutbox().then((sent) => setSyncMessage(sent ? 'millisecond row written to edge' : 'local buffer retained'));
    if (currentIndex + 1 >= schedule.length) {
      const allTrials = [...trials, trial];
      const sig = computeSignature(allTrials);
      setSignature(sig);
      await persistSummary(trial.sessionId, sig);
      void flushOutbox();
      void refreshCommons();
      setView('results');
    } else {
      setCurrentIndex((i) => i + 1);
    }
  };

  const localStats = useMemo(() => {
    const avg = trials.length ? trials.reduce((sum, t) => sum + t.warpRatio, 0) / trials.length : 1;
    return { avg, n: trials.length };
  }, [trials]);

  const startAllowed = Boolean(turnstileToken || !bootstrap?.turnstileEnabled) && !starting;

  return (
    <main className="app-shell">
      <header className="hero-bar">
        <div className="brand" aria-label="Driftglass">
          <img src="/favicon.svg" alt="" />
          <div>
            <strong>Driftglass</strong>
            <span>The Elastic Second</span>
          </div>
        </div>
        <nav aria-label="Primary">
          <button className="nav-link" onClick={() => setView('welcome')}>instrument</button>
          <button className="nav-link" onClick={refreshCommons}>commons</button>
          <button className="nav-link" onClick={() => setView('results')} disabled={!signature}>signature</button>
          <a className="nav-link social-link" href={LINKEDIN_URL} target="_blank" rel="noreferrer">LinkedIn</a>
          <a className="nav-link support-link" href={COFFEE_URL} target="_blank" rel="noreferrer">Buy me a coffee</a>
        </nav>
      </header>

      <section className="status-strip" aria-live="polite">
        <span>identity: {deviceHashPrefix ? `${deviceHashPrefix}…` : 'anonymous device key local'}</span>
        <span>sync: {syncMessage}</span>
        <span>local avg drift: {localStats.n ? `${Math.round((localStats.avg - 1) * 100)}% · ${localStats.n} trials` : 'none yet'}</span>
      </section>

      {error && <div className="error-card" role="alert">{error}</div>}

      {view === 'welcome' && (
        <div className="welcome-grid">
          <section className="intro-card">
            <p className="kicker">A psychophysics game for the commons</p>
            <h1>Your seconds are not the clock’s seconds.</h1>
            <p className="lead">
              Driftglass measures subjective duration with blind reproduction trials across calm, signal pressure,
              flow, and oddball novelty. You get a time-distortion signature; the commons gets aggregate cognition.
            </p>
            <div className="phenomena-grid">
              <div><strong>Internal clock</strong><span>Arousal can dilate felt duration.</span></div>
              <div><strong>Flow compression</strong><span>Absorbed attention can make time vanish.</span></div>
              <div><strong>Oddball dilation</strong><span>Novel events can feel longer than repeated ones.</span></div>
            </div>
            <div className="consent-line">
              <span>Consent:</span> {consentLine}
            </div>
            {bootstrap && (
              <TurnstileBox
                siteKey={bootstrap.turnstileSiteKey}
                enabled={bootstrap.turnstileEnabled}
                onToken={setTurnstileToken}
              />
            )}
            <div className="button-row">
              <button className="primary" onClick={beginRun} disabled={!startAllowed}>{starting ? 'opening…' : 'verify + begin'}</button>
              <a className="primary coffee-cta" href={COFFEE_URL} target="_blank" rel="noreferrer">support the project</a>
              <a className="ghost social-cta" href={LINKEDIN_URL} target="_blank" rel="noreferrer">connect on LinkedIn</a>
              <button className="ghost" onClick={() => { resetDeviceKey(); resetRun(); setTurnstileToken(''); }}>reset anonymous device</button>
            </div>
          </section>

          <aside className="oracle-card">
            <p className="kicker">Creator</p>
            <h2>Mindful Abhay</h2>
            <p>
              Driftglass is an open cognition instrument. Follow the work or fund future experiments.
            </p>
            <div className="creator-links" aria-label="Creator links">
              <a className="creator-link support" href={COFFEE_URL} target="_blank" rel="noreferrer">
                <strong>Buy me a coffee</strong>
                <span>Back the commons</span>
              </a>
              <a className="creator-link" href={LINKEDIN_URL} target="_blank" rel="noreferrer">
                <strong>LinkedIn</strong>
                <span>Connect with Abhay</span>
              </a>
            </div>
            <p className="kicker">What you get</p>
            <ul>
              <li>A four-axis radial time signature.</li>
              <li>Indices for signal stretch, flow compression, novelty dilation, and clock precision.</li>
              <li>Local-first persistence if the connection drops.</li>
              <li>No account, no email, no private leaderboard.</li>
            </ul>
          </aside>
        </div>
      )}

      {view === 'run' && currentPlan && sessionId && deviceKey && (
        <TrialRunner
          key={currentPlan.id}
          plan={currentPlan}
          sessionId={sessionId}
          deviceKey={deviceKey}
          total={schedule.length}
          onComplete={completeTrial}
        />
      )}

      {view === 'run' && completed && signature && <SignatureChart signature={signature} />}

      {view === 'results' && (
        <div className="results-grid">
          {signature ? (
            <>
              <SignatureChart signature={signature} />
              <section className="export-card">
                <p className="kicker">Keep your copy</p>
                <h2>Export your local signature</h2>
                <p>Your browser has the full local record. Export it whenever you want.</p>
                <button className="primary" onClick={() => {
                  const blob = new Blob([JSON.stringify({ signature, trials }, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `driftglass-${signature.archetype.toLowerCase().replaceAll(' ', '-')}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}>download JSON</button>
              </section>
            </>
          ) : (
            <section className="intro-card"><h2>No signature yet</h2><p>Run the instrument first.</p></section>
          )}
          <CommonsPanel commons={commons} loading={commonsLoading} onRefresh={refreshCommons} />
        </div>
      )}

      {commons && view !== 'results' && <CommonsPanel commons={commons} loading={commonsLoading} onRefresh={refreshCommons} />}

      <footer className="footer-note">
        Built by <a href={LINKEDIN_URL} target="_blank" rel="noreferrer">Mindful Abhay</a>. Support the work at <a href={COFFEE_URL} target="_blank" rel="noreferrer">Buy Me a Coffee</a>.
        <span> Cloudflare Pages + Workers + D1 + KV + Analytics Engine + Turnstile.</span>
      </footer>
    </main>
  );
}
